import React, { useState } from 'react';
import {
  Cog6ToothIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

// Default color palette for user selection
const defaultColorPalette = [
  '#DC2626', // Red
  '#2563EB', // Blue
  '#7C3AED', // Purple
  '#059669', // Green
  '#F59E0B', // Amber
  '#EC4899', // Pink
  '#F97316', // Orange
  '#06B6D4', // Cyan
  '#8B5CF6', // Violet
  '#6B7280', // Gray
  '#84CC16', // Lime
  '#14B8A6', // Teal
];

// Color picker component
function ColorPicker({ color, onChange, colors = defaultColorPalette }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-6 h-6 rounded border-2 border-white shadow-sm"
        style={{ backgroundColor: color }}
      />
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 top-8 z-20 bg-white rounded-lg shadow-lg p-2 grid grid-cols-4 gap-1">
            {colors.map((c) => (
              <button
                key={c}
                onClick={() => {
                  onChange(c);
                  setIsOpen(false);
                }}
                className={`w-6 h-6 rounded ${c === color ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Editable legend item
function LegendItem({
  value,
  label,
  color,
  count,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onColorChange,
  onLabelChange,
  editLabel
}) {
  return (
    <div className="flex items-center justify-between py-1.5 group">
      <div className="flex items-center flex-1">
        {isEditing ? (
          <>
            <ColorPicker color={color} onChange={onColorChange} />
            <input
              type="text"
              value={editLabel}
              onChange={(e) => onLabelChange(e.target.value)}
              className="ml-2 flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </>
        ) : (
          <>
            <div
              className="w-4 h-4 rounded-full mr-2 border border-gray-200 cursor-pointer hover:ring-2 hover:ring-blue-300"
              style={{ backgroundColor: color }}
              onClick={() => onEdit(value)}
            />
            <span className="text-sm text-gray-600">{label}</span>
          </>
        )}
      </div>

      <div className="flex items-center gap-1">
        {count !== undefined && !isEditing && (
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded mr-1">
            {count.toLocaleString()}
          </span>
        )}

        {isEditing ? (
          <>
            <button
              onClick={() => onSave(value)}
              className="p-1 text-green-600 hover:bg-green-50 rounded"
            >
              <CheckIcon className="h-4 w-4" />
            </button>
            <button
              onClick={onCancel}
              className="p-1 text-gray-500 hover:bg-gray-100 rounded"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </>
        ) : (
          <div className="opacity-0 group-hover:opacity-100 flex items-center">
            <button
              onClick={() => onEdit(value)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            >
              <PencilIcon className="h-3 w-3" />
            </button>
            <button
              onClick={() => onDelete(value)}
              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
            >
              <TrashIcon className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MapLegend({
  colorBy = 'party',
  colorScheme = {},
  labels = {},
  voterStats = {},
  onSchemeChange,
  onColorByChange,
  availableFields = []
}) {
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editColor, setEditColor] = useState('');
  const [newItemValue, setNewItemValue] = useState('');
  const [newItemLabel, setNewItemLabel] = useState('');
  const [newItemColor, setNewItemColor] = useState('#6B7280');
  const [showAddNew, setShowAddNew] = useState(false);

  // Get current scheme entries
  const schemeEntries = Object.entries(colorScheme).filter(([key]) => key !== 'default');

  const handleEdit = (value) => {
    setEditingItem(value);
    setEditLabel(labels[value] || value);
    setEditColor(colorScheme[value]);
  };

  const handleSave = (value) => {
    if (onSchemeChange) {
      onSchemeChange({
        ...colorScheme,
        [value]: editColor
      }, {
        ...labels,
        [value]: editLabel
      });
    }
    setEditingItem(null);
  };

  const handleCancel = () => {
    setEditingItem(null);
  };

  const handleDelete = (value) => {
    if (onSchemeChange) {
      const newScheme = { ...colorScheme };
      const newLabels = { ...labels };
      delete newScheme[value];
      delete newLabels[value];
      onSchemeChange(newScheme, newLabels);
    }
  };

  const handleColorChange = (color) => {
    setEditColor(color);
  };

  const handleAddNew = () => {
    if (newItemValue && onSchemeChange) {
      onSchemeChange({
        ...colorScheme,
        [newItemValue]: newItemColor
      }, {
        ...labels,
        [newItemValue]: newItemLabel || newItemValue
      });
      setNewItemValue('');
      setNewItemLabel('');
      setNewItemColor('#6B7280');
      setShowAddNew(false);
    }
  };

  const totalCount = Object.values(voterStats).reduce((a, b) => a + b, 0);

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-700">Legend</h3>
          {availableFields.length > 0 && (
            <select
              value={colorBy}
              onChange={(e) => onColorByChange && onColorByChange(e.target.value)}
              className="text-xs border border-gray-200 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              {availableFields.map(field => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </select>
          )}
        </div>
        <button
          onClick={() => setIsConfiguring(!isConfiguring)}
          className={`p-1.5 rounded transition-colors ${isConfiguring
              ? 'bg-blue-100 text-blue-600'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
          title="Configure colors"
        >
          <Cog6ToothIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Legend items */}
      <div className="p-3 space-y-1">
        {schemeEntries.map(([value, color]) => (
          <LegendItem
            key={value}
            value={value}
            label={labels[value] || value}
            color={editingItem === value ? editColor : color}
            count={voterStats[value]}
            isEditing={editingItem === value}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            onDelete={handleDelete}
            onColorChange={handleColorChange}
            onLabelChange={setEditLabel}
            editLabel={editLabel}
          />
        ))}

        {/* Default/Other category if present */}
        {colorScheme.default && voterStats.default > 0 && (
          <LegendItem
            value="default"
            label={labels.default || 'Other'}
            color={colorScheme.default}
            count={voterStats.default}
            isEditing={editingItem === 'default'}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            onDelete={handleDelete}
            onColorChange={handleColorChange}
            onLabelChange={setEditLabel}
            editLabel={editLabel}
          />
        )}

        {/* Add new category (when configuring) */}
        {isConfiguring && (
          <>
            {showAddNew ? (
              <div className="pt-2 border-t border-gray-100 mt-2">
                <div className="flex items-center gap-2 mb-2">
                  <ColorPicker color={newItemColor} onChange={setNewItemColor} />
                  <input
                    type="text"
                    placeholder="Value (e.g., R)"
                    value={newItemValue}
                    onChange={(e) => setNewItemValue(e.target.value)}
                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Label (e.g., Republican)"
                    value={newItemLabel}
                    onChange={(e) => setNewItemLabel(e.target.value)}
                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                  <button
                    onClick={handleAddNew}
                    disabled={!newItemValue}
                    className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                  >
                    <CheckIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setShowAddNew(false)}
                    className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddNew(true)}
                className="w-full mt-2 pt-2 border-t border-gray-100 flex items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-700"
              >
                <PlusIcon className="h-3 w-3" />
                Add category
              </button>
            )}
          </>
        )}
      </div>

      {/* Total */}
      {totalCount > 0 && (
        <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 font-medium">Total Voters</span>
            <span className="text-gray-900 font-semibold">
              {totalCount.toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
